import { useEffect, useState, type ReactNode } from "react";

// Read-only mermaid renderer shared by the flow-overview graphs (version
// flow, RC flow) and by ```mermaid fences in ProseMarkdown. Mermaid is
// loaded lazily so it stays out of the main bundle and only ships when a
// graph is actually on screen. `fallback` renders when the code fails to
// parse (hand-written PRD diagrams), instead of silently rendering nothing.

// mermaid.render needs a fresh element id per call.
let renderSeq = 0;

export const MermaidGraph = ({
  code,
  fallback,
}: {
  code: string;
  fallback?: ReactNode;
}) => {
  const [svg, setSvg] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    (async () => {
      const mermaid = (await import("mermaid")).default;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "neutral",
        flowchart: { padding: 8 },
      });
      const { svg } = await mermaid.render(`flow-graph-${++renderSeq}`, code);
      if (!cancelled) setSvg(svg);
    })().catch(() => {
      if (cancelled) return;
      setSvg("");
      setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (failed) return <>{fallback}</>;
  if (!svg) return null;

  return (
    <div
      className="not-typeset my-3 overflow-x-auto rounded-md border bg-background p-3 [&_svg]:max-w-none"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default MermaidGraph;
